import React, { useState } from "react";
import PropTypes from "prop-types";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { ORGANIZATION_TYPE } from "@constants";
import { OrganizationService } from "@services";
import utils from "@utils";
import { Modal, Switch, Tooltip } from "../../../../../components";
import InvoicingSection from "./InvoicingSection";
import "./OrganizationSettingsModal.scss";

const OrganizationSettingsModal = props => {
	const { lang } = global;
	const [checkingOrganizationFutureOfficerJobs, setCheckingOrganizationFutureOfficerJobs] = useState(false);

	const { show, details, Type, closeModal, updateDetails, isFinancialAdmin } = props;

	const { LAW_ENFORCEMENT_DEPARTMENT, COORDINATOR } = ORGANIZATION_TYPE;

	const {
		NonRKPayInvoicingEnabled,
		NonRKPayPaymentsEnabled,
		SameDayPayEnabled,
		CanExceedRecurrentJobCreation,
		StripeSubscriptionEnabled,
		FloatInvoicePayEnabled,
		UsePTAEOCode,
		PayCodesEnabled,
		OnDutyUserScheduleEnabled,
		OfficerPortalEnabled,
		MFAEnforced,
		InvoicingV3Enabled,
		IsFederated,
		VehicleInvoicingEnabled,
		OfficerInvoicingV3Enabled,
		RKVerifiesEnabled,
		WorkerCompensationFeeEnabled,
		InstantPayEnabledV3,
		InstantPayEnabledOfficerV3,
		ManualReconciliationEnabled,
		AgencyInvoicingDisputes,
	} = details;

	const { flags } = props;
	const {
		federation: federationFlag,
		enableOfficerWebPortalToggle,
		officerInvoicingV3,
		workersCompensationFee,
		instantPayV3,
		enforceV2NonrkpayInvoicingEnabled,
		agencyInvoicingDisputesToggle,
	} = flags;

	const checkOrganizationHasClientsWithFutureOfficerInvoicingJobs = async () => {
		setCheckingOrganizationFutureOfficerJobs(true);
		const { Id } = details;
		try {
			const { data } = await OrganizationService.hasClientsWithFutureOfficerInvoicingJobs(Id);

			if (data.HasFutureOfficerInvoicingJobs) {
				utils.notify({
					type: "error",
					message: data.Message,
				});
			} else {
				updateDetails("ManualReconciliationEnabled")({ target: { value: undefined } });
			}
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error checking for future officer invoicing jobs"),
			});
		} finally {
			setCheckingOrganizationFutureOfficerJobs(false);
		}
	};

	const handleOnClickManualReconciliation = (event) => {
		if (OfficerInvoicingV3Enabled && !ManualReconciliationEnabled) {
			checkOrganizationHasClientsWithFutureOfficerInvoicingJobs();
		} else {
			updateDetails("ManualReconciliationEnabled")(event);
		}
	};

	const isV4Migrated = details?.V4Migrated;

	return (
		<Modal
			id="organization-settings-modal"
			dialogClassName="organization-settings-dialog"
			show={show}
			size="lg"
			onHide={() => closeModal()}
			closeModal={() => closeModal()}
		>
			<Modal.Header
				closeButton
				className="hidden-print"
			>
				<Modal.Title>{lang.t("Organization Settings")}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="container-fluid">
				<div style={{ marginLeft: "15px", marginRight: "15px" }}>
					<If condition={isV4Migrated}>
						<InvoicingSection
							details={details}
							Type={Type}
							updateDetails={updateDetails}
							flags={flags}
							handleOnClickManualReconciliation={handleOnClickManualReconciliation}
							checkingOrganizationFutureOfficerJobs={checkingOrganizationFutureOfficerJobs}
						/>
					</If>
					<If condition={!isV4Migrated}>
						<h5>
							<strong>{lang.t("Invoicing Type")}:</strong>
						</h5>
						<div className="row row-header">
							<If condition={Type === LAW_ENFORCEMENT_DEPARTMENT || Type === COORDINATOR}>
								<div className="col-md-3 col-xs-6 status-indicator">
									<Switch
										on={NonRKPayInvoicingEnabled}
										onClick={updateDetails("NonRKPayInvoicingEnabled")}
									/>
									<br />
									<small>{lang.t("Manual Invoicing")}</small>
								</div>
								<div className="col-md-3 col-xs-6 status-indicator">
									<Switch
										disabled={!NonRKPayInvoicingEnabled}
										on={NonRKPayPaymentsEnabled}
										onClick={updateDetails("NonRKPayPaymentsEnabled")}
									/>
									<br />
									<small>{lang.t("Manual Payments")}</small>
								</div>
							</If>
							<div className="col-md-3 col-xs-6 status-indicator">
								<Switch
									disabled={!NonRKPayInvoicingEnabled || !NonRKPayPaymentsEnabled}
									on={InvoicingV3Enabled}
									onClick={updateDetails("InvoicingV3Enabled")}
								/>
								<br />
								<small>{lang.t("InvoiceV3")}</small>
							</div>
							<If condition={officerInvoicingV3}>
								<div className="col-md-3 col-xs-6 status-indicator">
									<Switch
										disabled={
											!NonRKPayInvoicingEnabled ||
											!NonRKPayPaymentsEnabled ||
											!InvoicingV3Enabled
										}
										on={OfficerInvoicingV3Enabled}
										onClick={updateDetails("OfficerInvoicingV3Enabled")}
									/>
									<br />
									<small>{lang.t("OfficerInvoicing")}</small>
								</div>
							</If>
						</div>
					</If>
					<h5>
						<strong>{lang.t("Authentication Settings")}:</strong>
					</h5>
					<div className="row row-header">
						<div className="col-md-4 col-xs-6 status-indicator">
							<Switch
								on={MFAEnforced}
								onClick={updateDetails("MFAEnforced")}
							/>
							<Tooltip content={lang.t("Enforce 2FA Tooltip")}>
								<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
							</Tooltip>
							<br />
							<small>{lang.t("Enforce 2FA")}</small>
						</div>
						<If condition={federationFlag}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={IsFederated}
									disabled
								/>
								<Tooltip content={lang.t("Federation Tooltip")}>
									<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
								<br />
								<small>{lang.t("Federation")}</small>
							</div>
						</If>
					</div>
					<h5>
						<strong>{lang.t("Verification Settings")}:</strong>
					</h5>
					<div className="row row-header">
						<div className="col-md-4 col-xs-6 status-indicator">
							<Switch
								on={RKVerifiesEnabled}
								onClick={updateDetails("RKVerifiesEnabled")}
							/>
							<If condition={!RKVerifiesEnabled}>
								<Tooltip content={lang.t("Verification Settings Tooltip Off")}>
									<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</If>
							<If condition={RKVerifiesEnabled}>
								<Tooltip content={lang.t("Verification Settings Tooltip On")}>
									<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</If>
							<br />
							<small>{lang.t("RollKallVerifies")}</small>
						</div>
					</div>
					<h5>
						<strong>{lang.t("Other Settings")}:</strong>
					</h5>
					<div className="row row-header">
						<If condition={!isV4Migrated}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={SameDayPayEnabled}
									onClick={updateDetails("SameDayPay")}
								/>
								<br />
								<small>{lang.t("InstantPayV1")}</small>
							</div>
						</If>
						<If condition={!isV4Migrated && !!instantPayV3}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={InstantPayEnabledV3}
									onClick={updateDetails("InstantPayEnabledV3")}
									disabled={!SameDayPayEnabled || !InvoicingV3Enabled}
								/>
								<br />
								<small>{lang.t("InstantPayV3")}</small>
							</div>
						</If>
						<If condition={!isV4Migrated && !!officerInvoicingV3}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={InstantPayEnabledOfficerV3}
									onClick={updateDetails("InstantPayEnabledOfficerV3")}
									disabled={!OfficerInvoicingV3Enabled}
								/>
								<br />
								<small>{lang.t("Officer v3 InstantPay")}</small>
							</div>
						</If>
						<If condition={!isV4Migrated && !!enforceV2NonrkpayInvoicingEnabled}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={ManualReconciliationEnabled}
									onClick={handleOnClickManualReconciliation}
									disabled={!InvoicingV3Enabled || checkingOrganizationFutureOfficerJobs}
									loading={checkingOrganizationFutureOfficerJobs}
								/>
								<br />
								<small>{lang.t("Default Manual Reconcilliation")}</small>
							</div>
						</If>
						<div className="col-md-4 col-xs-6 status-indicator">
							<Switch
								on={CanExceedRecurrentJobCreation}
								onClick={updateDetails("CanExceedRecurrentJobCreation")}
							/>
							<br />
							<small>{lang.t("Show Jobs 180+ Days")}</small>
						</div>
						<If condition={Type === LAW_ENFORCEMENT_DEPARTMENT}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={StripeSubscriptionEnabled}
									onClick={updateDetails("StripeSubscriptionEnabled")}
								/>
								<br />
								<small>{lang.t("Stripe Subscriptions")}</small>
							</div>
						</If>
						{/* Show only to designated super admins */}
						<If condition={Type === LAW_ENFORCEMENT_DEPARTMENT && isFinancialAdmin()}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={FloatInvoicePayEnabled}
									onClick={updateDetails("FloatInvoicePayEnabled")}
								/>
								<br />
								<small>{lang.t("Float")}</small>
							</div>
						</If>
						<If condition={!isV4Migrated}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={UsePTAEOCode}
									onClick={updateDetails("UsePTAEOCode")}
								/>
								<br />
								<small>{lang.t("PTAEO codes")}</small>
							</div>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={PayCodesEnabled}
									onClick={updateDetails("PayCodesEnabled")}
								/>
								<br />
								<small>{lang.t("Pay Codes")}</small>
							</div>
						</If>
						<If condition={Type === LAW_ENFORCEMENT_DEPARTMENT}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={OnDutyUserScheduleEnabled}
									disabled
								/>
								<Tooltip content={lang.t("External Schedule Tooltip")}>
									<i className="rkf rkf-question-solid job-form-tooltip tooltip-icon" />
								</Tooltip>
								<br />
								<small>{lang.t("External Schedule")}</small>
							</div>
						</If>
						<If
							condition={
								Type === LAW_ENFORCEMENT_DEPARTMENT && enableOfficerWebPortalToggle
							}
						>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									on={OfficerPortalEnabled}
									onClick={updateDetails("OfficerPortalEnabled")}
								/>
								<br />
								<small>{lang.t("Officer Web Portal")}</small>
							</div>
						</If>
						<If condition={!isV4Migrated}>
							<div className="col-md-4 col-xs-6 status-indicator">
								<Switch
									disabled={!InvoicingV3Enabled}
									on={VehicleInvoicingEnabled}
									onClick={updateDetails("VehicleInvoicingEnabled")}
								/>
								<br />
								<small>{lang.t("Vehicle Invoicing: Officer Pay Opt-Out")}</small>
							</div>
							<If condition={workersCompensationFee}>
								<div className="col-md-4 col-xs-6 status-indicator">
									<Switch
										on={WorkerCompensationFeeEnabled}
										onClick={updateDetails("WorkerCompensationFeeEnabled")}
									/>
									<br />
									<small>{lang.t("Workers' Compensation Fee")}</small>
								</div>
							</If>
							{agencyInvoicingDisputesToggle &&
								(Type === LAW_ENFORCEMENT_DEPARTMENT || Type === COORDINATOR) && (
									<div className="col-md-3 col-xs-6 status-indicator">
										<Switch
											disabled={
												!NonRKPayInvoicingEnabled ||
												!NonRKPayPaymentsEnabled ||
												!InvoicingV3Enabled
											}
											on={AgencyInvoicingDisputes}
											onClick={updateDetails("AgencyInvoicingDisputes")}
										/>
										<br />
										<small>{lang.t("Agency Invoice Disputes (beta)")}</small>
									</div>
								)}
						</If>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					id="button-print-table"
					className="btn btn-primary hidden-print"
					type="button"
					onClick={() => closeModal()}
				>
					{lang.t("Done")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

OrganizationSettingsModal.propTypes = {
	show: PropTypes.bool,
	details: PropTypes.object,
	Type: PropTypes.any,
	closeModal: PropTypes.func,
	updateDetails: PropTypes.func,
	isFinancialAdmin: PropTypes.func,
	flags: PropTypes.object.isRequired,
};

OrganizationSettingsModal.defaultProps = {
	show: false,
	details: {},
	Type: null,
	closeModal: () => {},
	updateDetails: () => {},
	isFinancialAdmin: () => {},
};

export default withLDConsumer()(OrganizationSettingsModal);
